import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faSync, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import API_BASE_URL from "../../config";
import './SwitchLog.css';

const SwitchLog = ({ onBack }) => {
    const [logs, setLogs] = useState([]);
    const [switchList, setSwitchList] = useState([]);
    const [visibleCount, setVisibleCount] = useState(50);
    const [loading, setLoading] = useState(false);
    const [selectedSwitch, setSelectedSwitch] = useState("all");

    // Fetch Switch List for Dropdown (OLT-এর মতো করে ব্যাকএন্ড/লগ ডাটা থেকে ড্রপডাউন লোড)
    useEffect(() => {
        const fetchSwitchList = async () => {
            try {
                // ডিভাইস লিস্ট বা লাইভ লগ থেকে সুইচের নাম লোড করা
                const response = await fetch(`${API_BASE_URL}/log-monitor/switch`);
                const resData = await response.json();
                
                const rawList = Array.isArray(resData) ? resData : [];
                if (rawList.length > 0) {
                    const sorted = [...new Set(rawList.map(d => d.hostname))]
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                    
                    setSwitchList(sorted);
                }
            } catch (err) { 
                console.error("Switch List Fetch Error:", err); 
            }
        };
        fetchSwitchList();
    }, []);

    // Fetch Switch Logs from Database
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const url = selectedSwitch === "all" 
                ? `${API_BASE_URL}/log-monitor/switch` 
                : `${API_BASE_URL}/log-monitor/switch/${selectedSwitch}`;

            const response = await fetch(url);
            const data = await response.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch (err) { 
            console.error("Switch Logs Fetch Error:", err);
            setLogs([]); 
        } finally { 
            setLoading(false); 
        }
    }, [selectedSwitch]);

    // Handle Auto-Refresh interval
    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 15000);
        return () => clearInterval(interval);
    }, [fetchLogs]);

    // Export Table Data to CSV Format
    const exportToCSV = () => {
        if (logs.length === 0) return;
        const headers = ["HOSTNAME", "DATE", "TIME", "PORT", "LOG MESSAGE\n"];
        const rows = logs.map(log => {
            const logDate = log.date || "";
            const logTime = log.time || "";
            const portDetail = log.port || "SWITCH";
            const cleanMsg = log.message ? log.message.replace(/"/g, '""') : "";

            return `"${log.hostname || ''}","${logDate}","${logTime}","${portDetail}","${cleanMsg}"`;
        });
        
        const blob = new Blob([headers.join(",") + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `SWITCH_${selectedSwitch}_LOGS.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="sw-main-container">
            {/* Header Controls Layout */}
            <div className="sw-header-wrapper">
                <div className="sw-header-inner">
                    <button className="sw-back-btn" onClick={onBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>

                    <h2 className="sw-page-title">SWITCH LIVE LOGS</h2>

                    <div className="sw-header-controls">
                        <select 
                            className="sw-dropdown" 
                            value={selectedSwitch} 
                            onChange={(e) => {
                                setSelectedSwitch(e.target.value);
                                setVisibleCount(50);
                            }}
                        >
                            <option value="all">ALL SWITCHES</option>
                            {switchList.map(name => (
                                <option key={name} value={name}>{name.toUpperCase()}</option>
                            ))}
                        </select>

                        <button 
                            className="sw-icon-btn refresh" 
                            title="Refresh" 
                            onClick={fetchLogs} 
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>

                        <button 
                            className="sw-icon-btn export" 
                            title="Export CSV" 
                            onClick={exportToCSV} 
                            disabled={logs.length === 0}
                        >
                            <FontAwesomeIcon icon={faFileExport} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Logs Table / List Area */}
            <div className="sw-log-display-area">
                <div className="sw-table-header">
                    <div className="sw-h-col host">HOSTNAME</div>
                    <div className="sw-h-col time">TIMESTAMP</div>
                    <div className="sw-h-col port">PORT</div>
                    <div className="sw-h-col msg">LOG MESSAGE</div>
                </div>

                <div className="sw-rows-wrapper">
                    {logs.slice(0, visibleCount).map((log, index) => {
                        const logText = (log.message || "").toLowerCase();
                        let statusClass = "default";
                        if (logText.includes("up") || logText.includes("online") || logText.includes("success")) statusClass = "online";
                        if (logText.includes("down") || logText.includes("offline") || logText.includes("failed")) statusClass = "offline";

                        return (
                            <div key={log.id || index} className={`sw-log-row status-${statusClass}`}>
                                <div className="sw-b-col host">{log.hostname}</div>
                                <div className="sw-b-col time">
                                    <small className="sw-date-label">{log.date}</small>
                                    <div className="sw-time-label">{log.time}</div>
                                </div>
                                <div className="sw-b-col port">
                                    {log.port && log.port !== "SWITCH" ? log.port : "SWITCH"}
                                </div>
                                <div className="sw-b-col msg">{log.message}</div>
                            </div>
                        );
                    })}

                    {logs.length === 0 && !loading && (
                        <div className="sw-no-data">
                            No switch logs found.
                        </div>
                    )}
                </div>

                {logs.length > visibleCount && (
                    <div className="sw-load-more-container">
                        <button className="sw-load-more-btn" onClick={() => setVisibleCount(v => v + 50)}>
                            Load More Logs
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SwitchLog;